import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faBarsFilter, faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { BadgeNumeric, ButtonIcon, InputText, MultipleSelectInput, Text, Tooltip } from "@performyard/py-components";
import { ReviewDetailsFrontend } from "@performyard/service-contracts";

import DownloadButton from "../../../../components.smart/DownloadButton";
import { AIButton } from "../../../../components/AIModules/AIButton/AIButton";
import { AIPromotionalButton } from "../../../../components/AIModules/AIPromotionalButton/AIPromotionalButton";
import Spacer from "../../../../components/Spacer";
import ConfirmDeleteReviewModal from "./ConfirmDeleteModal";
import ReviewsDetailsTable from "./ReviewsDetailsTable";

import { FilterFormSchema } from "./FilterPanel/types";

import {
	useAiSettings,
	useDeleteReviewFromReviewCycle,
	useReviewCycleDetails,
	useReviewCycleReviewsDetailsStream,
} from "../../../../api/hooks";
import { useAiPromotion } from "../../../../hooks/useAiPromotion";
import useSearchQueryParams from "../../../../hooks/useSearchQueryParams";

import { exportReviewCycleReviewReportsDetailsCsv } from "../../../../api/services/defs/internal-api/review-cycles";

import pubSub from "../../../../utils/PubSub";
import { generateChips, getActiveFiltersCount } from "./FilterPanel/utils";

import ReviewCycleReviewsDetailsTableFilterPanel from "./FilterPanel/FilterPanel";
import { getColumns } from "./columns";

type ReviewCycleReviewsDetailsTableProps = {
	reviewCycleId: string;
};

const ReviewCycleReviewsDetailsTable = ({ reviewCycleId }: ReviewCycleReviewsDetailsTableProps) => {
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: FilterFormSchema });

	const removeArrayValue = (key: keyof FilterFormSchema, value: string) => {
		if (Array.isArray(queryParams[key])) {
			handleQueryChange({ [key]: queryParams[key].filter((id) => id !== value) });
		}
	};

	// Ref for the AG Grid instance (used to call csv export)
	const gridRef = useRef<AgGridReact<ReviewDetailsFrontend>>(null);
	// Column configuration
	const [showUnassigned, setShowUnassigned] = useState<boolean>(false);
	const [showExternal, setShowExternal] = useState<boolean>(false);
	const [showThreeSixty, setShowThreeSixty] = useState<boolean>(false);
	const [showAwaitingApproval, setShowAwaitingApproval] = useState<boolean>(false);

	// API hooks
	const { data: reviewCycleDetails } = useReviewCycleDetails(reviewCycleId);
	const { data: reviewCycleReviewsDetails } = useReviewCycleReviewsDetailsStream(reviewCycleId);
	const { mutate: deleteReview, isPending: isDeleteReviewPending } = useDeleteReviewFromReviewCycle();

	// AI settings
	const { data: aiSettings } = useAiSettings();

	// AI promotion
	const { isAiPromotion, promotionId, userEmail } = useAiPromotion();

	// Page state
	const [reviewIdToDelete, setReviewIdToDelete] = useState<string | null>(null);
	const [downloadJobId, setDownloadJobId] = useState<string | null>(null);

	// Check if AI Insights button should be shown
	const showAIInsightsButton = aiSettings?.reviewCycleInsightsEnabled;

	// Cycle insights can only be generated when the cycle is completed (or closed, which sets status to "Completed")
	const isCycleInsightsDisabled = reviewCycleDetails?.status !== "Completed";

	// Count reviews with at least one author-completed form (for AI insights validation)
	const authorCompletedReviewCount = useMemo(() => {
		if (!reviewCycleReviewsDetails) return 0;
		return reviewCycleReviewsDetails.filter((review) => review.authorTurnStats?.completed >= 1).length;
	}, [reviewCycleReviewsDetails]);

	// Filter
	const [debouncedSubjectSearch] = useDebounceValue(queryParams.search, 200);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);

	const subjectGroupOptions = useMemo(() => {
		const options: Parameters<typeof MultipleSelectInput<string>>[0]["options"] = [];

		const uniqueSubjectGroupIds = new Set<string>();
		reviewCycleReviewsDetails?.forEach((review) => {
			if (review.subjectGroup?._id) {
				if (!uniqueSubjectGroupIds.has(review.subjectGroup._id)) {
					uniqueSubjectGroupIds.add(review.subjectGroup._id);
					options.push({
						label: review.subjectGroup.name ?? "",
						value: review.subjectGroup._id,
					});
				}
			}
		});
		return options;
	}, [reviewCycleReviewsDetails]);

	// Chip components for each filter, skipping search
	const chips = useMemo(() => {
		return generateChips(queryParams, subjectGroupOptions, removeArrayValue, handleQueryChange);
	}, [JSON.stringify(Object.entries(queryParams).filter(([key]) => key !== "search"))]);

	const activeFiltersCount = getActiveFiltersCount(queryParams);

	const handleDeleteReview = (reviewId: string) => {
		setReviewIdToDelete(reviewId);
	};

	const [columnData, setColumnData] = useState<ColDef[]>(
		getColumns({
			handleDeleteReview,
			showUnassigned,
			showExternal,
			showThreeSixty,
			showAwaitingApproval,
		})
	);

	// Update column visibility depending on data
	useEffect(() => {
		if (!reviewCycleReviewsDetails?.length) return;
		// Check for column visibility
		for (const item of reviewCycleReviewsDetails) {
			if (!showUnassigned && item.unassignedFormsCount > 0) {
				setShowUnassigned(true);
			}
			if (!showExternal && item.externalFormsCount > 0) {
				setShowExternal(true);
			}
			if (!showThreeSixty && item.threeSixtyFormsCount > 0) {
				setShowThreeSixty(true);
			}
			if (!showAwaitingApproval && item.pendingThreeSixtyFormsCount > 0) {
				setShowAwaitingApproval(true);
			}
		}
	}, [reviewCycleReviewsDetails]);

	// Update column data when showUnassigned, showExternal, showThreeSixty, or showAwaitingApproval changes
	useEffect(() => {
		setColumnData(
			getColumns({
				handleDeleteReview,
				showUnassigned,
				showExternal,
				showThreeSixty,
				showAwaitingApproval,
			})
		);
	}, [showUnassigned, showExternal, showThreeSixty, showAwaitingApproval]);

	// Handle CSV download
	const handleDownloadCsv = async () => {
		try {
			const response = await exportReviewCycleReviewReportsDetailsCsv(reviewCycleId, {
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});

			pubSub.publish("check async frequency", { frequency: 500 });
			setDownloadJobId(response.jobId);

			return response;
		} catch (error) {
			console.error("Error downloading CSV:", error);
			return null;
		}
	};

	return (
		<div className="flex-col gap-2">
			<div className="flex flex-wrap justify-between gap-2">
				<div className="flex flex-wrap gap-2">
					<div className="w-full min-w-[278px] max-w-[278px]">
						<InputText
							id="search"
							placeholder="Search subjects..."
							aria-label="Search subjects"
							leftIcon={faMagnifyingGlass}
							value={queryParams.search}
							onChange={(value) => handleQueryChange({ search: value })}
							clearable={!!queryParams.search && queryParams.search !== ""}
						/>
					</div>
					<div className="relative">
						<ButtonIcon
							size={24}
							variant="outline"
							icon={faBarsFilter}
							dangerouslySetClassName="w-8 h-8"
							onClick={() => setIsFilterPanelOpen(true)}
							aria-label="Open filters"
						/>
						{activeFiltersCount > 0 && <BadgeNumeric count={activeFiltersCount} />}
					</div>
				</div>
				<div className="flex w-fit gap-2">
					{showAIInsightsButton && (
						<Tooltip
							content="Complete or close the cycle to generate insights."
							disabled={!isCycleInsightsDisabled}
						>
							<AIButton
								moduleType="cycleInsights"
								cycleId={reviewCycleId}
								buttonSize="medium"
								authorCompletedReviewCount={authorCompletedReviewCount}
								disabled={isCycleInsightsDisabled}
							/>
						</Tooltip>
					)}
					{isAiPromotion && (
						<AIPromotionalButton
							label="AI Insights"
							promotionId={promotionId}
							userEmail={userEmail}
							buttonSize="medium"
						/>
					)}
					<DownloadButton
						jobID={downloadJobId}
						autoDownload={true}
						onClick={handleDownloadCsv}
						onDownload={() => setDownloadJobId(null)}
						key="download-csv"
						testid="download-csv"
					>
						Download CSV
					</DownloadButton>
				</div>
			</div>
			{chips.length > 0 && (
				<>
					<Spacer height={2} />
					<div className="flex w-full flex-wrap items-center gap-1">
						<Text size="paragraph-3" weight="medium">
							Filters Applied:
						</Text>
						{chips}
					</div>
				</>
			)}
			<Spacer height={2} />
			<ReviewsDetailsTable
				queryParams={{ ...queryParams, search: debouncedSubjectSearch }}
				columnData={columnData}
				gridRef={gridRef}
			/>
			<ConfirmDeleteReviewModal
				isOpen={!!reviewIdToDelete}
				isPending={isDeleteReviewPending}
				handleClose={() => setReviewIdToDelete(null)}
				onSubmit={() => {
					if (reviewIdToDelete) {
						deleteReview({ reviewCycleId, reviewId: reviewIdToDelete });
						setReviewIdToDelete(null);
					}
				}}
			/>
			<ReviewCycleReviewsDetailsTableFilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				queryParams={queryParams}
				handleQueryChange={handleQueryChange}
				subjectGroupOptions={subjectGroupOptions}
			/>
		</div>
	);
};

export default ReviewCycleReviewsDetailsTable;
